import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import SearchBar from "shared/SearchBar";
import Button, { ButtonPreset } from "shared/Button";
import SpinningIcon from "shared/SpinningIcon";
import PlusSquareIcon from "shared/assets/plus-square.svg";
import AssignedAudioRow from "DreamCast/Shared/AssignedAudioRow";
import formatDate from "shared/@utils/formatDate";
import styles from "./PlaylistAudiosSection.module.css";

function reorderByIds(items, sourceId, targetId, position = "below") {
  const sourceIndex = items.findIndex((item) => item.mediaItemId === sourceId);

  if (sourceIndex < 0 || sourceId === targetId) {
    return items;
  }

  const nextItems = [...items];
  const [moved] = nextItems.splice(sourceIndex, 1);
  const targetIndex = nextItems.findIndex(
    (item) => item.mediaItemId === targetId,
  );

  if (targetIndex < 0) {
    return items;
  }

  const insertionIndex = position === "above" ? targetIndex : targetIndex + 1;
  nextItems.splice(insertionIndex, 0, moved);
  return nextItems;
}

export function PlaylistAudiosSection({
  audios,
  searchValue,
  onSearch,
  onAdd,
  onRemove,
  onReorder,
  title,
  className,
  isLoadingAudioSelection,
}) {
  const [draggedAudioId, setDraggedAudioId] = useState(null);
  const [dropTargetAudioId, setDropTargetAudioId] = useState(null);
  const [dropTargetPosition, setDropTargetPosition] = useState(null);

  const filteredAudios = useMemo(() => {
    const normalizedQuery = searchValue.trim().toLowerCase();

    if (!normalizedQuery) {
      return audios;
    }

    return audios.filter((audio) => {
      const formattedDate = audio.date ? formatDate(audio.date, "M/DD/YYYY") : "";
      const searchable =
        `${audio.title} ${audio.date} ${formattedDate} ${audio.publishOn} ${audio.contributorName} ${audio.episodePart} ${audio.sku}`.toLowerCase();
      return searchable.includes(normalizedQuery);
    });
  }, [audios, searchValue]);

  const handleDragOver = (event, targetAudioId) => {
    event.preventDefault();

    if (draggedAudioId && draggedAudioId !== targetAudioId) {
      const targetRect = event.currentTarget.getBoundingClientRect();
      const isAboveMiddle =
        event.clientY < targetRect.top + targetRect.height / 2;

      setDropTargetAudioId(targetAudioId);
      setDropTargetPosition(isAboveMiddle ? "above" : "below");
    }
  };

  const handleDrop = (targetAudioId) => {
    if (!draggedAudioId || draggedAudioId === targetAudioId) {
      setDraggedAudioId(null);
      setDropTargetAudioId(null);
      setDropTargetPosition(null);
      return;
    }

    const reordered = reorderByIds(
      audios,
      draggedAudioId,
      targetAudioId,
      dropTargetPosition || "below",
    );

    onReorder(reordered);
    setDraggedAudioId(null);
    setDropTargetAudioId(null);
    setDropTargetPosition(null);
  };

  const handleDragEnd = () => {
    setDraggedAudioId(null);
    setDropTargetAudioId(null);
    setDropTargetPosition(null);
  };

  return (
    <section className={`${styles.card} ${className || ""}`.trim()}>
      <div className={styles.headerRow}>
        <h3 className={styles.title}>{title}</h3>
        <div className={styles.tools}>
          <div className={styles.searchWrap}>
            <SearchBar
              placeholderText="Search Media"
              value={searchValue}
              onInput={onSearch}
              minChar={0}
              searchDelay={0}
            />
          </div>

          <Button
            className={styles.addButton}
            onClick={onAdd}
            disabled={isLoadingAudioSelection}
            preset={ButtonPreset.addWithIcon}
            icon={isLoadingAudioSelection ? <SpinningIcon size={16} /> : <img src={PlusSquareIcon} alt="" />}
          >
            {!isLoadingAudioSelection && "Add"}
          </Button>
        </div>
      </div>

      <div className={styles.divider} />

      {filteredAudios.length === 0 ? (
        <p className={styles.emptyState}>No Media Added</p>
      ) : (
        <div
          className={[styles.rows, draggedAudioId ? styles.rowsDragging : ""]
            .filter(Boolean)
            .join(" ")}
        >
          {filteredAudios.map((audio) => (
            <div
              key={audio.mediaItemId}
              className={[
                styles.rowWrap,
                draggedAudioId === audio.mediaItemId ? styles.dragging : "",
                dropTargetAudioId === audio.mediaItemId
                  ? styles.dropTarget
                  : "",
                dropTargetAudioId === audio.mediaItemId &&
                dropTargetPosition === "above"
                  ? styles.dropAbove
                  : "",
                dropTargetAudioId === audio.mediaItemId &&
                dropTargetPosition === "below"
                  ? styles.dropBelow
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              draggable
              onDragStart={() => setDraggedAudioId(audio.mediaItemId)}
              onDragEnd={handleDragEnd}
              onDragOver={(event) => handleDragOver(event, audio.mediaItemId)}
              onDrop={() => handleDrop(audio.mediaItemId)}
            >
              <AssignedAudioRow
                thumbnail={audio.thumbnailPath}
                title={audio.title}
                date={audio.date}
                speaker={audio.contributorName}
                episodePart={audio.episodePart}
                onRemove={() => onRemove(audio.mediaItemId)}
                draggable
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

PlaylistAudiosSection.propTypes = {
  audios: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      thumbnail: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      speaker: PropTypes.string.isRequired,
      episodePart: PropTypes.string,
    }),
  ),
  searchValue: PropTypes.string,
  onSearch: PropTypes.func,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  onReorder: PropTypes.func,
  title: PropTypes.string,
  className: PropTypes.string,
  isLoadingAudioSelection: PropTypes.bool,
};

PlaylistAudiosSection.defaultProps = {
  audios: [],
  searchValue: "",
  onSearch: () => {},
  onAdd: () => {},
  onRemove: () => {},
  onReorder: () => {},
  title: "Playlist Media",
  className: "",
  isLoadingAudioSelection: false,
};

export default PlaylistAudiosSection;
